import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CancelTokenSource } from 'axios'
import { RecipientTypeEnum } from 'Modules/Communication/shared/models/Inbox'
import {
  IRecipientModel,
  RecipientOpenState,
  IRecipientQueryModel,
  FilterTypeEnum,
  RecipientsSortByEnum,
  IBuildingTypes,
  IObjectItemModel,
  IMeterItemModel,
  IFrequentRecipientsModel,
} from 'Modules/Communication/shared/models/Recipients'
import { ICommittee } from 'Modules/HousingAssociation/BoardAndCommittees/shared/models/Committees'
import { ICommonListModel } from 'shared/models/Common'
import { ISortOrderEnum } from 'shared/models/List'
import { IAddress } from 'shared/models/Unit'
import { RecipientState } from './selectors'
import { DEFAULT_PAGE_TAKE } from 'shared/constants/filters'

const initialState: RecipientState = {
  data: null,
  loading: false,
  error: null,
  filters: {
    skip: 0,
    take: DEFAULT_PAGE_TAKE,
    searchText: '',
    sortcolumn: RecipientsSortByEnum.Name,
    sortorder: ISortOrderEnum.asc,
    filterType: FilterTypeEnum.Owners,
    buildingTypeId: null,
    buildingId: null,
    addressId: null,
    boardId: null,
    meterTypeNo: null,
    objectTypeNo: null,
  },

  openState: 'Email',
  openStateType: RecipientTypeEnum.To,
  listedRecipients: [],
  listedRecipientsRemovalQueue: [],
  frequentRecipients: {
    sms: {
      data: [],
      loading: false,
      error: null,
    },
    fetchedSmsWithClientId: null,
    email: {
      data: [],
      loading: false,
      error: null,
    },
    fetchedEmailWithClientId: null,
  },
  clientAddresses: {
    data: [],
    loading: false,
    error: null,
  },

  buildingTypes: {
    data: [],
    loading: false,
    error: null,
  },
  committees: {
    data: [],
    loading: false,
    error: null,
  },
  objects: [],
  meters: [],
}

const messageRecipientsSlice = createSlice({
  name: 'messageRecipients',
  initialState,
  reducers: {
    /**
     * Fetch
     */
    fetchRequest: (state, _: PayloadAction<CancelTokenSource>) => {
      // Resets the boardId if the filterType is changed away from BoardMembers
      if (state.filters.filterType !== FilterTypeEnum.BoardMembers) {
        state.filters.boardId = null
      }

      state.data = null
      state.loading = true
      state.error = null
    },
    fetchSuccess: (
      state,
      action: PayloadAction<ICommonListModel<IRecipientModel>>
    ) => {
      state.data = action.payload
      state.loading = false
      state.error = null
    },
    fetchError: (state, action) => {
      state.loading = false
      state.error = action.payload
    },

    /**
     * Filter
     */
    setFilters: (
      state,
      action: PayloadAction<
        IRecipientQueryModel | { [x: string]: number | string | null }
      >
    ) => {
      const isSwappingFilterType = Object.keys(action.payload).includes(
        'filterType'
      )

      // Sets default values for pre-fetched lists connected to filterType
      if (isSwappingFilterType) {
        const filterType = action.payload.filterType
        if (
          filterType === FilterTypeEnum.Meters &&
          state.meters.length &&
          !state.filters.meterTypeNo
        ) {
          state.filters.meterTypeNo = state.meters[0].meterTypeId!
        }

        if (
          filterType === FilterTypeEnum.Objects &&
          state.objects.length &&
          !state.filters.objectTypeNo
        ) {
          state.filters.objectTypeNo = state.objects[0].objectTypeId!
        }

        if (
          filterType === FilterTypeEnum.BoardMembers &&
          state.committees.data?.length &&
          !state.filters.boardId
        ) {
          state.filters.boardId = 'all'
        }
      }

      const isNotHabitants =
        isSwappingFilterType &&
        (action.payload.filterType !== (FilterTypeEnum.Owners as string) ||
          action.payload.filterType !== (FilterTypeEnum.Tenants as string) ||
          action.payload.filterType !== (FilterTypeEnum.Cohabitant as string))

      if (isNotHabitants) {
        state.filters.addressId = null
        state.filters.buildingTypeId = null
      }

      // Checks whenever we swap filters that should reset the skip value
      const filtersToResetOn: string[] = [
        'boardId',
        'addressId',
        'buildingTypeId',
        'buildingId',
        'filterType',
        'meterTypeNo',
        'objectTypeNo',
        'searchText',
      ]
      const isSwappingFilterThatShouldReset = Object.keys(action.payload).map(
        (load) => filtersToResetOn.some((l) => l === load)
      )
      if (isSwappingFilterThatShouldReset) state.filters.skip = 0

      // only update filters sent in
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },

    /**
     * Reset
     */
    resetListedRecipients: (state) => {
      state.listedRecipients = []
      state.listedRecipientsRemovalQueue = []
      state.openStateType = null
    },
    reset: (state) => {
      state = initialState
    },
    resetObjectAndMetersFilter: (state) => {
      state.filters.meterTypeNo = null
      state.filters.objectTypeNo = null
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },
    onRecipientsClose: (state) => {
      state.data = null
      state.listedRecipients = []
      state.listedRecipientsRemovalQueue = []
    },

    /**
     * Handle recipient
     */
    addRecipient: (state, action: PayloadAction<IRecipientModel>) => {
      state.listedRecipients.push(action.payload)
    },
    removeRecipient: (state, action: PayloadAction<string>) => {
      state.listedRecipients = state.listedRecipients.filter(
        (recipient) => recipient.nameId !== action.payload
      )
    },

    addRecipientToRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel>
    ) => {
      state.listedRecipientsRemovalQueue.push(action.payload)
    },
    removeRecipientFromRemovalQueue: (state, action: PayloadAction<string>) => {
      state.listedRecipientsRemovalQueue =
        state.listedRecipientsRemovalQueue.filter(
          (recipient) => recipient.nameId !== action.payload
        )
    },

    addRecipientsToRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel[]>
    ) => {
      // add action.payload to removal queue without duplicates
      state.listedRecipientsRemovalQueue = [
        ...state.listedRecipientsRemovalQueue,
        ...action.payload.filter(
          (recipient) =>
            !state.listedRecipientsRemovalQueue.some(
              (r) =>
                r.nameId === recipient.nameId ||
                r.email === recipient.email ||
                r.phonenumber === recipient.phonenumber
            )
        ),
      ]
    },

    removeRecipientsFromRemovalQueue: (
      state,
      action: PayloadAction<IRecipientModel[]>
    ) => {
      state.listedRecipientsRemovalQueue =
        state.listedRecipientsRemovalQueue.filter(
          (recipient) =>
            !action.payload.some(
              (r) =>
                r.nameId === recipient.nameId ||
                r.email === recipient.email ||
                r.phonenumber === recipient.phonenumber
            )
        )
    },

    removeAllRecipients: (state) => {
      // Remove all recipients from listedRecipients that exist in data.result
      state.listedRecipients = state.listedRecipients.filter(
        (recipient) =>
          !state.data?.result.find(
            (listedRecipient) => listedRecipient.nameId === recipient.nameId
          )
      )
    },
    addAllRecipients: (state, action: PayloadAction<IRecipientModel[]>) => {
      action.payload.forEach((recipient) => {
        if (
          state.listedRecipients.some(
            ({ nameId }) => recipient.nameId && nameId === recipient.nameId
          )
        )
          return

        state.listedRecipients.push(recipient)
      })
    },
    handleSubmitEmail: (
      _state,
      _action: PayloadAction<{ overrideRecipientType?: RecipientTypeEnum }>
    ) => {},
    handleSubmitSms: (_state) => {},

    /**
     * Modal actions
     * Handles the open state of the recipient modal, using saga to open/close the modal
     */
    handleRecipientModal: (
      state,
      action: PayloadAction<{
        open: RecipientOpenState
        type: RecipientTypeEnum | null
      }>
    ) => {
      state.openState = action.payload.open
      state.openStateType = action.payload.type
    },

    /**
     * Frequent recipients
     */
    fetchFrequentSmsRecipientsRequest: (state) => {
      state.frequentRecipients.sms.loading = true
      state.frequentRecipients.sms.error = null
    },

    fetchFrequentSmsRecipientsSuccess: (
      state,
      action: PayloadAction<{
        data: IFrequentRecipientsModel[]
        clientId: string
      }>
    ) => {
      state.frequentRecipients.sms.data = action.payload.data
      state.frequentRecipients.sms.loading = false
      state.frequentRecipients.sms.error = null
      state.frequentRecipients.fetchedSmsWithClientId = action.payload.clientId
    },

    fetchFrequentSmsRecipientsError: (state, action) => {
      state.frequentRecipients.sms.loading = false
      state.frequentRecipients.sms.error = action.payload
    },

    fetchFrequentEmailRecipientsRequest: (state) => {
      state.frequentRecipients.email.loading = true
      state.frequentRecipients.email.error = null
    },

    fetchFrequentEmailRecipientsSuccess: (
      state,
      action: PayloadAction<{
        data: IFrequentRecipientsModel[]
        clientId: string
      }>
    ) => {
      state.frequentRecipients.email.data = action.payload.data
      state.frequentRecipients.email.loading = false
      state.frequentRecipients.email.error = null
      state.frequentRecipients.fetchedEmailWithClientId =
        action.payload.clientId
    },

    fetchFrequentEmailRecipientsError: (state, action) => {
      state.frequentRecipients.email.loading = false
      state.frequentRecipients.email.error = action.payload
    },

    /**
     * Client addresses
     * Re-used from housing unit, in case the store has not been initiated there, this is the safest way of handling it.
     */
    fetchClientAddressesRequest: (state) => {
      state.clientAddresses.loading = true
      state.clientAddresses.error = null
    },
    fetchClientAddressesSuccess: (state, action: PayloadAction<IAddress[]>) => {
      state.clientAddresses.data = action.payload
      state.clientAddresses.loading = false
      state.clientAddresses.error = null
    },
    fetchClientAddressesError: (state, action) => {
      state.clientAddresses.loading = false
      state.clientAddresses.error = action.payload
    },

    /**
     * Building types
     */
    fetchBuildingTypesRequest: (state) => {
      state.buildingTypes.loading = true
      state.buildingTypes.error = null
    },
    fetchBuildingTypesSuccess: (
      state,
      action: PayloadAction<IBuildingTypes[]>
    ) => {
      state.buildingTypes.data = action.payload
      state.buildingTypes.loading = false
      state.buildingTypes.error = null
    },
    fetchBuildingTypesError: (state, action) => {
      state.buildingTypes.loading = false
      state.buildingTypes.error = action.payload
    },

    /**
     * Commmittees
     */
    fetchCommmitteesRequest: (state) => {
      state.committees.loading = true
      state.committees.error = null
    },
    fetchCommmitteesSuccess: (state, action: PayloadAction<ICommittee[]>) => {
      state.committees.data = action.payload
      state.committees.loading = false
      state.committees.error = null
    },
    fetchCommmitteesError: (state, action) => {
      state.committees.loading = false
      state.committees.error = action.payload
    },

    /**
     * Objects and meters
     */
    fetchObjectAndMetersTypesRequest: () => {},
    fetchObjectAndMetersTypesSuccess: (
      state,
      action: PayloadAction<{
        objects: IObjectItemModel[]
        meters: IMeterItemModel[]
      }>
    ) => {
      // Set filters to first object type number, avoid refresh of filters
      if (action.payload.objects.length) {
        state.filters.objectTypeNo = action.payload.objects[0].objectTypeId!
      } else {
        state.filters.objectTypeNo = undefined
      }

      if (action.payload.meters.length) {
        state.filters.meterTypeNo = action.payload.meters[0].meterTypeId!
      } else {
        state.filters.meterTypeNo = undefined
      }

      state.objects = action.payload.objects
      state.meters = action.payload.meters
    },
    fetchObjectAndMetersTypesError: (state) => {
      state.filters.objectTypeNo = undefined
    },
  },
})

export default messageRecipientsSlice
